import React, {
  memo,
  useEffect,
  useMemo,
  useState,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react';
import Head from 'next/head';
import cn from 'classnames';
import { useRouter } from 'next/router';
import useSWR from 'swr';
import { useTranslation } from 'next-i18next';
import { AxiosResponse } from 'axios';
import { format } from 'date-fns';

import { Icon, IconButton, Spinner, Image, Tooltip } from 'components';
import { Row } from 'components/layout';
import { CircularProgressBar } from 'components/CircularProgressBar/CircularProgressBar.component';
import { ButtonV3 } from 'components/ComponentV2';
import ToolFilters from 'components/ToolFilters';
import { Input } from 'components/form';
import { ProgressBar } from 'components/ComponentV2/ProgressBar/ProgressBar.component';
import CandidateList from 'components/InvitePopup/CandidateList';
import { useVisibleOnIntersect } from 'hooks/useVisibleOnIntersect';
import { SidebarOption, useToolFilters } from 'hooks/useToolFilters';
import { SocietyTabsType } from 'hooks/useCampaigns.hook';
import { useInviteGuest } from 'hooks/useInviteGuests';
import InviteViaManualEmail from 'pages/campaigns/[id]/InviteViaManualEmail';
import { API_ROUTES } from 'lib/api-routes';
import { apiInstance } from 'lib/utils/axios';
import { useCampaigns } from 'lib/contexts/campaigns-context';
import { PAGE_ROUTES } from 'lib/page-routes';
import { TalentPoolsResponse } from 'lib/models/talent-pool';
import { OpportunityAttributes } from 'lib/models/opportunity';
import { InviteCandidate } from 'lib/models/candidate';
import { parseResponse } from 'lib/utils/parser';
import { EventTouchpointable } from 'lib/models/event';
import { CohostAttributes } from 'lib/models/cohost';
import { useAuth } from 'lib/providers/AuthProvider';
import { formatToK } from 'utils/format';
import styles from './Invite.module.scss';

const PER_PAGE_COUNT = 20;

export interface InviteProps {
  /** Custom class name */
  className?: string;
  touchpoint: OpportunityAttributes;
  candidateSelected: Array<InviteCandidate>;
  setCandidateSelected: Dispatch<SetStateAction<Array<InviteCandidate>>>;
  importedCandidateList: Array<InviteCandidate>;
  setImportedCandidateList: Dispatch<SetStateAction<Array<InviteCandidate>>>;
  onClose: () => void;
  onModalResolve: () => void;
  onNextClick: () => void;
}

export const Invite: React.FC<InviteProps> = memo(
  ({
    className = '', // custom class name
    touchpoint,
    candidateSelected,
    setCandidateSelected,
    importedCandidateList,
    setImportedCandidateList,
    onClose,
    onModalResolve,
    onNextClick,
  }: InviteProps) => {
    const contentClassNames = cn(
      {
        [styles.Invite]: true,
      },
      className
    );
    const router = useRouter();
    const [t] = useTranslation('invite-popup');
    const [tCommon] = useTranslation('common');

    const { id: touchpointId, past_event_id } = router.query;

    const {
      eventsList: pastEvents,
      setSelectedEventState,
      isAPILoading: isPastEventsLoading,
    } = useCampaigns();
    const { updateNotificationViewed } = useInviteGuest();

    const { user, managerSociety } = useAuth();
    const managerId = user?.id || '';
    const isHuzzleVerified = managerSociety?.is_huzzle_verified;

    const [sidebarOptionSelected, setSidebarOptionSelected] =
      useState<SidebarOption>(SidebarOption.EXPLORE_HUZZLE);

    const [pastEventSelected, setPastEventSelected] = useState<{
      id: string;
      title: string;
      applications: number;
    } | null>(null);
    const [showNotification, setShowNotification] = useState(false);

    const useBottomRef = useRef<HTMLDivElement | null>(null);
    const topRef = useRef<HTMLDivElement | null>(null);

    const {
      huzzle_invite_limit_left,
      member_invite_limit_left,
      touchpointable,
    } = useMemo(() => {
      const {
        available_huzzle_invite_limit,
        available_member_invite_limit,
        touchpointable,
        cohost_application,
      } = touchpoint || {};

      const getCohostLimit = () => {
        if (cohost_application) {
          const {
            available_huzzle_invite_limit: cohost_available_huzzle_invite_limit,
            available_member_invite_limit: cohost_available_member_invite_limit,
          } = parseResponse<CohostAttributes>(cohost_application);
          return {
            cohost_available_huzzle_invite_limit,
            cohost_available_member_invite_limit,
          };
        } else return {};
      };
      const {
        cohost_available_huzzle_invite_limit,
        cohost_available_member_invite_limit,
      } = getCohostLimit();

      const huzzle_invite_limit_left = cohost_available_huzzle_invite_limit
        ? cohost_available_huzzle_invite_limit
        : available_huzzle_invite_limit;
      const member_invite_limit_left = cohost_available_member_invite_limit
        ? cohost_available_member_invite_limit
        : available_member_invite_limit;

      return {
        huzzle_invite_limit_left,
        member_invite_limit_left,
        touchpointable,
      };
    }, []);

    const { guide_viewed_user_ids } =
      parseResponse(touchpointable as EventTouchpointable) || {};

    useEffect(() => {
      if (
        guide_viewed_user_ids?.length === 0 ||
        !guide_viewed_user_ids?.includes(managerId)
      ) {
        setShowNotification(true);
      } else setShowNotification(false);
    }, [guide_viewed_user_ids, managerId]);

    const {
      talentPool,
      isLoading,
      isLoadingMore,
      hasMoreRecords,
      filters,
      setFilters,
      search,
      setSearch,
      fetchMoreData,
      onChangeFilter,
      partialFilters,
      partialCount,
      isPartialLoading,
      onChangeGroupedFilter,
    } = useToolFilters({
      transaction: 'invite_guests',
      sidebarOptionSelected,
    });

    useVisibleOnIntersect({
      elRef: useBottomRef,
      threshold: 0,
      onIntersect: fetchMoreData,
      rootMargin: '100px',
    });

    const resetHandler = () => {
      setSearch('');
      setFilters(null);
      setImportedCandidateList([]);
      topRef?.current?.scrollIntoView({ behavior: 'auto' });
    };

    const onExploreHuzzleClick = () => {
      resetHandler();
      setSidebarOptionSelected(SidebarOption.EXPLORE_HUZZLE);
    };
    const onCommunityClick = () => {
      resetHandler();
      setSidebarOptionSelected(SidebarOption.COMMUNITY);
    };
    const onManualEmailClick = () => {
      resetHandler();
      setSidebarOptionSelected(SidebarOption.MANUAL_EMAIL);
    };

    useEffect(() => {
      setSelectedEventState(SocietyTabsType.ENDED);
    }, []);

    useEffect(() => {
      if (past_event_id && sidebarOptionSelected !== SidebarOption.PAST_EVENT) {
        router.replace(`${PAGE_ROUTES.CAMPAIGNS}/${touchpointId}`, undefined, {
          shallow: true,
        });
        setPastEventSelected(null);
      }
    }, [past_event_id, sidebarOptionSelected]);

    const pastEventsList = useMemo(() => {
      return pastEvents.slice(0, 20).map((event) => {
        const { id, title, start_date, applications } = event;
        const startDateValue = format(new Date(start_date), 'dd MMM');

        return (
          <div
            key={id}
            className={cn(styles.pastEvents, {
              [styles.pastEventsActive]:
                sidebarOptionSelected === SidebarOption.PAST_EVENT &&
                id === past_event_id,
            })}
            onClick={() => {
              setSearch('');
              setPastEventSelected({
                id,
                title,
                applications,
              });
              router.replace(
                `${PAGE_ROUTES.CAMPAIGNS}/${touchpointId}?past_event_id=${id}`,
                undefined,
                {
                  shallow: true,
                }
              );
              setSidebarOptionSelected(SidebarOption.PAST_EVENT);
            }}
          >
            <p className={styles.pastEventsName}>{title}</p>
            <p className={styles.pastEventsDetail}>
              {startDateValue}
              {' · '}
              {applications} Guests
            </p>
          </div>
        );
      });
    }, [pastEvents, past_event_id, sidebarOptionSelected]);

    const onHideNotificationClickHandler = async () => {
      try {
        await updateNotificationViewed(touchpointId as string);
      } catch (error) {
        console.error(error);
      }
    };

    const notificationStub = (
      <Row
        align="center"
        justify="space-between"
        columnGap={12}
        isFullWidthRow
        wrap={false}
        className={styles.notificationStub}
      >
        <p>{t('notification-stub.text')}</p>
        <ButtonV3
          color="secondary"
          size="small"
          borderless
          onClick={() => {
            setShowNotification(false);
            onHideNotificationClickHandler();
          }}
        >
          {t('notification-stub.button-text')}
        </ButtonV3>
      </Row>
    );

    const [isFilterOpen, setFilterOpen] = useState<boolean>(false);

    const { data: exploreResponse } = useSWR<
      AxiosResponse<TalentPoolsResponse>
    >(
      `${API_ROUTES.SOCIETY_TALENT_POOL}?with_in_touchpoint_id=${touchpointId}&page=1&per_page=1`,
      apiInstance.get,
      {
        revalidateOnFocus: false,
      }
    );

    const { data: communityResponse } = useSWR<
      AxiosResponse<TalentPoolsResponse>
    >(
      `${API_ROUTES.SOCIETY_COMMUNITY_POOL}?with_in_touchpoint_id=${touchpointId}&status=subscribed&with_details=true&page=1&per_page=1`,
      apiInstance.get,
      {
        revalidateOnFocus: false,
      }
    );

    const exploreHuzzleCount = useMemo(
      () => exploreResponse?.data?.meta?.total || 0,
      [exploreResponse]
    );
    const communityCount = useMemo(
      () => communityResponse?.data?.meta?.total || 0,
      [communityResponse]
    );

    const inviteLimit = useMemo(() => {
      if (sidebarOptionSelected === SidebarOption.EXPLORE_HUZZLE)
        return huzzle_invite_limit_left;
      else if (sidebarOptionSelected === SidebarOption.COMMUNITY)
        return member_invite_limit_left;
      else return talentPool?.length;
    }, [
      huzzle_invite_limit_left,
      member_invite_limit_left,
      sidebarOptionSelected,
      talentPool,
    ]);

    const { totalInviteLeft, percentInviteLeft } = useMemo(() => {
      const totalInvites = huzzle_invite_limit_left + member_invite_limit_left;
      /** candidate Selected From Explore Huzzle And Community */
      const reqdCandidateSelected = candidateSelected.filter(
        (item) =>
          item.sidebarOptionSelected === SidebarOption.EXPLORE_HUZZLE ||
          item.sidebarOptionSelected === SidebarOption.COMMUNITY
      );

      const totalInviteLeft = totalInvites - reqdCandidateSelected.length;

      const percentInviteLeft = (totalInviteLeft / totalInvites) * 100;

      return { totalInviteLeft, percentInviteLeft };
    }, [huzzle_invite_limit_left, member_invite_limit_left, candidateSelected]);

    const { noOfExploreHuzzleSelected, noOfCommunityMemberSelected } =
      useMemo(() => {
        const noOfExploreHuzzleSelected = candidateSelected.filter(
          (item) => item.sidebarOptionSelected === SidebarOption.EXPLORE_HUZZLE
        ).length;
        const noOfCommunityMemberSelected = candidateSelected.filter(
          (item) => item.sidebarOptionSelected === SidebarOption.COMMUNITY
        ).length;

        return { noOfExploreHuzzleSelected, noOfCommunityMemberSelected };
      }, [candidateSelected]);

    const invitePopover = (
      <div className={styles['popover-invite-left']}>
        <p className={styles.title}>{t('header.invite-popover.title')}</p>
        <div className={styles.inviteCountContainer}>
          <p className={styles.inviteCountContainerTitle}>
            {t('header.invite-popover.subtitle1')}
            <span>{t('header.invite-popover.user1')}</span>
          </p>
          <Row align="center" justify="space-between">
            <p className={styles.inviteCount}>
              <span className={styles.count}>{noOfExploreHuzzleSelected} </span>
              <span className={styles.countText}>
                {t('header.invite-popover.selected')}
              </span>
            </p>
            <p className={styles.inviteCount}>
              <span className={styles.count}>{huzzle_invite_limit_left} </span>
              <span className={styles.countText}>
                {t('header.invite-popover.available')}
              </span>
            </p>
          </Row>
          <ProgressBar
            color="Primary"
            percentCompleted={
              (noOfExploreHuzzleSelected / huzzle_invite_limit_left) * 100
            }
            showProgressWhen0={false}
            className={styles.progressBar}
          />
        </div>
        <div className={styles.inviteCountContainer}>
          <p className={styles.inviteCountContainerTitle}>
            {t('header.invite-popover.subtitle1')}
            <span>{t('header.invite-popover.user2')}</span>
          </p>
          <Row align="center" justify="space-between">
            <p className={styles.inviteCount}>
              <span className={styles.count}>
                {noOfCommunityMemberSelected}{' '}
              </span>
              <span className={styles.countText}>
                {t('header.invite-popover.selected')}
              </span>
            </p>
            <p className={styles.inviteCount}>
              <span className={styles.count}>{member_invite_limit_left} </span>
              <span className={styles.countText}>
                {t('header.invite-popover.available')}
              </span>
            </p>
          </Row>
          <ProgressBar
            color="Primary"
            percentCompleted={
              (noOfCommunityMemberSelected / member_invite_limit_left) * 100
            }
            showProgressWhen0={false}
            className={styles.progressBar}
          />
        </div>
        {isHuzzleVerified ? null : (
          <>
            <p className={styles.subtitle}>
              {t('header.invite-popover.subtitle2')}
            </p>
            <p className={styles.subtitleDesc}>
              {t('header.invite-popover.desc')}
            </p>
            <ButtonV3
              onClick={() => window.open(PAGE_ROUTES.DASHBOARD, '_blank')}
            >
              {t('header.invite-popover.button-text')}
            </ButtonV3>
          </>
        )}
      </div>
    );

    const communityEmptyState = (
      <div className={styles['empty-state']}>
        <Image
          width="125"
          height="80"
          src="desert.png"
          alt="No Results Found"
          source="aws"
        />
        <p className={styles.title}>{t('no-member')}</p>
        <Row columnGap={16}>
          <ButtonV3
            startIcon="icon_mail"
            onClick={() => setSidebarOptionSelected(SidebarOption.MANUAL_EMAIL)}
          >
            {t('sidebar.enter-emails')}
          </ButtonV3>
          <ButtonV3
            color="secondary"
            startIcon="icon_compass"
            onClick={() =>
              setSidebarOptionSelected(SidebarOption.EXPLORE_HUZZLE)
            }
          >
            {t('sidebar.explore-huzzle')}
          </ButtonV3>
        </Row>
      </div>
    );

    const renderCandidateList = useMemo(() => {
      return (
        <CandidateList
          talentPool={talentPool}
          candidateSelected={candidateSelected}
          setCandidateSelected={setCandidateSelected}
          onModalResolve={onModalResolve}
          sidebarOptionSelected={sidebarOptionSelected}
          inviteLimit={inviteLimit}
          showCheckboxonExplore={true}
        />
      );
    }, [
      talentPool,
      candidateSelected,
      setCandidateSelected,
      inviteLimit,
      onModalResolve,
      sidebarOptionSelected,
    ]);

    const searchBoxPlaceholder = useMemo(() => {
      if (sidebarOptionSelected === SidebarOption.COMMUNITY) {
        return t('search-box.community-placeholder');
      } else if (
        sidebarOptionSelected === SidebarOption.PAST_EVENT &&
        pastEventSelected
      ) {
        return t('search-box.past-event-placeholder', {
          eventTitle: pastEventSelected.title,
        });
      }
    }, [sidebarOptionSelected, pastEventSelected]);

    const searchBox = (
      <Input
        className={styles['search-box']}
        inputClassName={styles['search-box-input']}
        placeholder={searchBoxPlaceholder}
        startIcon="icon_search"
        startIconSize="medium"
        startIconClassName={styles.searchIcon}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />
    );

    return (
      <>
        <Head>
          <title>Invite | Huzzle</title>
          <meta name="description" content="Huzzle" />
        </Head>
        <section className={contentClassNames} data-testid="Invite">
          <header className={styles.header}>
            <p>{t('header.title')}</p>
            <Row
              columnGap={8}
              align="center"
              wrap={false}
              className={styles.headerIcons}
            >
              <Tooltip
                content={invitePopover}
                yAlign="top"
                xAlign="right"
                className={styles.InvitePopover}
                containerClassName={styles.InvitePopoverContainer}
              >
                <div className={styles.headerDetails}>
                  {percentInviteLeft ? (
                    <CircularProgressBar
                      percentCompleted={percentInviteLeft}
                      size="xxSmall"
                      showChildren={false}
                      showIcon={false}
                      shouldStartFromTop
                      pathClassName={styles.progressCircle}
                    />
                  ) : null}
                  <p>
                    {t('header.invitation-left', {
                      invite_limit: totalInviteLeft,
                    })}
                  </p>
                </div>
              </Tooltip>
              <IconButton iconName="close" size="small" onClick={onClose} />
            </Row>
          </header>
          <div className={styles.content}>
            <div className={styles.sidebar}>
              <div className={styles.sidebarTop}>
                <Row
                  columnGap={10}
                  className={cn(styles.sidebarRow, {
                    [styles.sidebarRowActive]:
                      sidebarOptionSelected === SidebarOption.EXPLORE_HUZZLE,
                  })}
                  onClick={() => onExploreHuzzleClick()}
                >
                  <Icon
                    iconName={
                      sidebarOptionSelected === SidebarOption.EXPLORE_HUZZLE
                        ? 'icon_compass-filled'
                        : 'icon_compass'
                    }
                    className={cn({
                      [styles.sidebarIconActive]:
                        sidebarOptionSelected === SidebarOption.EXPLORE_HUZZLE,
                    })}
                  />
                  <p
                    className={cn(styles.sidebarText, {
                      [styles.sidebarTextActive]:
                        sidebarOptionSelected === SidebarOption.EXPLORE_HUZZLE,
                    })}
                  >
                    {t('sidebar.explore-huzzle')}
                  </p>
                  {exploreHuzzleCount ? (
                    <p className={styles.sidebarCount}>
                      {formatToK(exploreHuzzleCount)}
                    </p>
                  ) : null}
                </Row>

                <Row
                  columnGap={10}
                  className={cn(styles.sidebarRow, {
                    [styles.sidebarRowActive]:
                      sidebarOptionSelected === SidebarOption.COMMUNITY,
                  })}
                  onClick={() => onCommunityClick()}
                >
                  <Icon
                    key={sidebarOptionSelected}
                    iconName={
                      sidebarOptionSelected === SidebarOption.COMMUNITY
                        ? 'icon_multiple-11_filled'
                        : 'icon_multiple-11'
                    }
                    className={cn({
                      [styles.sidebarIconActive]:
                        sidebarOptionSelected === SidebarOption.COMMUNITY,
                    })}
                  />
                  <p
                    className={cn(styles.sidebarText, {
                      [styles.sidebarTextActive]:
                        sidebarOptionSelected === SidebarOption.COMMUNITY,
                    })}
                  >
                    {t('sidebar.community')}
                  </p>
                  {communityCount ? (
                    <p className={styles.sidebarCount}>
                      {formatToK(communityCount)}
                    </p>
                  ) : null}
                </Row>

                <Row
                  columnGap={10}
                  className={cn(styles.sidebarRow, {
                    [styles.sidebarRowActive]:
                      sidebarOptionSelected === SidebarOption.MANUAL_EMAIL,
                  })}
                  onClick={() => onManualEmailClick()}
                >
                  <Icon
                    key={sidebarOptionSelected}
                    iconName={
                      sidebarOptionSelected === SidebarOption.MANUAL_EMAIL
                        ? 'icon_mail_filled'
                        : 'icon_mail'
                    }
                    className={cn({
                      [styles.sidebarIconActive]:
                        sidebarOptionSelected === SidebarOption.MANUAL_EMAIL,
                    })}
                  />
                  <p
                    className={cn(styles.sidebarText, {
                      [styles.sidebarTextActive]:
                        sidebarOptionSelected === SidebarOption.MANUAL_EMAIL,
                    })}
                  >
                    {t('sidebar.enter-emails')}
                  </p>
                </Row>
                <div className={styles.separatorSidebar}></div>
                <p className={styles.pastEventsTitle}>
                  {t('sidebar.past-events')}
                </p>
              </div>
              <div className={styles.sidebarBottom}>
                {pastEvents.length === 0 && !isPastEventsLoading ? (
                  <p className={styles.pastEventsText}>
                    {t('sidebar.no-past-event')}
                  </p>
                ) : (
                  pastEventsList
                )}
              </div>
            </div>
            <div className={styles.separator}></div>
            <div className={styles.main}>
              {sidebarOptionSelected === SidebarOption.MANUAL_EMAIL ? (
                <InviteViaManualEmail
                  className={styles['invite-via-manual']}
                  candidateSelected={candidateSelected}
                  setCandidateSelected={setCandidateSelected}
                  importedCandidateList={importedCandidateList}
                  setImportedCandidateList={setImportedCandidateList}
                />
              ) : (
                <>
                  <div ref={topRef}></div>
                  {!isLoading &&
                  talentPool?.length === 0 &&
                  !search &&
                  sidebarOptionSelected === SidebarOption.COMMUNITY ? (
                    communityEmptyState
                  ) : (
                    <>
                      <div className={styles.fixContainer}>
                        {sidebarOptionSelected ===
                          SidebarOption.EXPLORE_HUZZLE && showNotification
                          ? notificationStub
                          : null}
                        {sidebarOptionSelected === SidebarOption.COMMUNITY ||
                        sidebarOptionSelected === SidebarOption.PAST_EVENT
                          ? searchBox
                          : null}
                        <Row
                          align="center"
                          justify="space-between"
                          isFullWidthRow
                          className={styles.buttonRow}
                        >
                          {sidebarOptionSelected ===
                          SidebarOption.PAST_EVENT ? (
                            <p className={styles.registeredCount}>
                              {t('registered-count', {
                                applications: pastEventSelected?.applications,
                              })}
                            </p>
                          ) : (
                            <Row align="center">
                              <ButtonV3
                                color="silent"
                                size="small"
                                borderless
                                onClick={() =>
                                  setFilterOpen((isFilterOpen) => !isFilterOpen)
                                }
                                startIcon="filter-2"
                                className={cn(styles['filter-btn'], {
                                  [styles.filterSelected]: isFilterOpen,
                                })}
                              >
                                {t('filters')}
                              </ButtonV3>
                              {isFilterOpen ? (
                                <ButtonV3
                                  variant="text"
                                  onClick={() => setFilters(null)}
                                  disabled={!filters}
                                  className={cn(styles.buttonText, {
                                    [styles.buttonTextDisabled]: !filters,
                                  })}
                                >
                                  {tCommon('clear-all')}
                                </ButtonV3>
                              ) : null}
                            </Row>
                          )}
                        </Row>
                        {isFilterOpen &&
                        sidebarOptionSelected !== SidebarOption.PAST_EVENT ? (
                          <ToolFilters
                            className={styles.filtersRow}
                            open={isFilterOpen}
                            filters={filters}
                            onChangeFilter={onChangeFilter}
                            isScroll={false}
                            isInvitePopup
                            showWhiteMultiselects
                            showFirstFilteredEntityName={false}
                            isMultiselectDisabled={false}
                            partialFilters={partialFilters}
                            partialCount={partialCount}
                            isPartialLoading={isPartialLoading}
                            onChangeGroupedFilter={onChangeGroupedFilter}
                          />
                        ) : null}
                        <div className={styles.separatorContent}></div>
                      </div>
                      {isLoading ? (
                        <Spinner wrapperClassname={styles.spinner} />
                      ) : (
                        <>
                          <div className={cn(styles.candidateListContainer)}>
                            {talentPool?.length === 0 ? (
                              <p className={styles.candidateListEmpty}>
                                {t('no-users-found')}
                              </p>
                            ) : null}
                            <div className={styles.candidateList}>
                              {renderCandidateList}
                              {hasMoreRecords && !isLoadingMore && (
                                <div ref={useBottomRef}></div>
                              )}
                            </div>
                          </div>
                          {isLoadingMore ? (
                            <Spinner wrapperClassname={styles.spinner} />
                          ) : null}
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
          <Row
            align="center"
            justify={candidateSelected?.length ? 'space-between' : 'end'}
            isFullWidthRow
            className={styles.footer}
          >
            {candidateSelected?.length ? (
              <Row align="center" className={styles.buttonRow}>
                <p>
                  {candidateSelected?.length} {t('selected')}
                </p>
                <ButtonV3
                  variant="text"
                  className={styles.buttonText}
                  onClick={() => setCandidateSelected([])}
                >
                  {tCommon('deselect')}
                </ButtonV3>
              </Row>
            ) : null}
            <ButtonV3
              onClick={onNextClick}
              disabled={candidateSelected?.length === 0}
            >
              {tCommon('next')}
            </ButtonV3>
          </Row>
        </section>
      </>
    );
  }
);

Invite.displayName = 'Invite';
